import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CheckoutData } from '../../../services/orderService';
import { useTranslation } from '../../../i18n';

interface CustomerStepProps {
    initialData: CheckoutData;
    loading: boolean;
    error: string | null;
    onBack: () => void;
    onSubmit: (data: CheckoutData) => void;
}

const CustomerStep: React.FC<CustomerStepProps> = ({
    initialData,
    loading,
    error,
    onBack,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<CheckoutData>(initialData);
    const [showBilling, setShowBilling] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        // Pre-fill from localStorage if available (logged in user data)
        const userEmail = localStorage.getItem('user_email');
        const userName = localStorage.getItem('user_name');

        if (userEmail && !formData.email) {
            setFormData(prev => ({ ...prev, email: userEmail }));
        }
        if (userName && !formData.name) {
            setFormData(prev => ({ ...prev, name: userName }));
        }
    }, []);

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (field.startsWith('billingAddress.')) {
            const addressField = field.replace('billingAddress.', '');
            setFormData(prev => ({
                ...prev,
                billingAddress: {
                    ...prev.billingAddress,
                    [addressField]: value,
                },
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.email || !formData.email.includes('@')) {
            errors.email = 'Bitte geben Sie eine gueltige E-Mail-Adresse ein';
        }

        if (!formData.name || formData.name.trim().length < 2) {
            errors.name = 'Bitte geben Sie Ihren Namen ein';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            onSubmit(formData);
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('checkout.customerForm.title')}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('checkout.customerForm.email')}
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        required
                        disabled={loading}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={t('checkout.customerForm.name')}
                        value={formData.name}
                        onChange={handleChange('name')}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name}
                        required
                        disabled={loading}
                    />
                </Grid>
            </Grid>

            <Accordion
                expanded={showBilling}
                onChange={() => setShowBilling(!showBilling)}
                sx={{ mt: 3 }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t('checkout.customerForm.billingAddress')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label={t('checkout.customerForm.street')}
                                value={formData.billingAddress?.street || ''}
                                onChange={handleChange('billingAddress.street')}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                fullWidth
                                label={t('checkout.customerForm.postalCode')}
                                value={formData.billingAddress?.postalCode || ''}
                                onChange={handleChange('billingAddress.postalCode')}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                fullWidth
                                label={t('checkout.customerForm.city')}
                                value={formData.billingAddress?.city || ''}
                                onChange={handleChange('billingAddress.city')}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label={t('checkout.customerForm.country')}
                                value={formData.billingAddress?.country || ''}
                                onChange={handleChange('billingAddress.country')}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    disabled={loading}
                >
                    {t('ra.action.back')}
                </Button>
                <Button
                    variant="contained"
                    endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    Weiter zur Zahlung
                </Button>
            </Box>
        </Box>
    );
};

export default CustomerStep;
